import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  ArrowUpRight,
  TrendingUp,
  Target,
  Building2,
  UsersRound,
  Plus,
} from 'lucide-react';
import { getCrmDashboard, getCrmStages } from '@/api/crm';
import { useCrmFormat, DEAL_STAGES } from './crm-format';
import { CrmActivityFeed } from './CrmActivityFeed';
import { CrmAttention } from './CrmAttention';
import {
  readValueMode,
  saveValueMode,
  stageArr,
  type CrmValueMode,
} from './crm-weighting';
import { CrmButton, CrmEmpty, CrmError, CrmSectionTitle } from './CrmShared';
import styles from './Crm.module.css';
export const CrmDashboard = ({
  onDeal,
  onCreate,
}: {
  onDeal: (id: string) => void;
  onCreate: () => void;
}) => {
  const { t, money } = useCrmFormat();
  const navigate = useNavigate();
  const dashboard = useQuery({
    queryKey: ['crm', 'dashboard'],
    queryFn: getCrmDashboard,
  });
  const stageRates = useQuery({
    queryKey: ['crm', 'stages'],
    queryFn: getCrmStages,
  });
  const [mode, setMode] = useState<CrmValueMode>(readValueMode);
  const chooseMode = (next: CrmValueMode) => {
    setMode(next);
    saveValueMode(next);
  };
  if (dashboard.isPending || stageRates.isPending)
    return (
      <p role="status" className={styles.loading}>
        {t('crm.loading')}
      </p>
    );
  if (dashboard.isError || stageRates.isError)
    return (
      <CrmError
        retry={() => {
          void dashboard.refetch();
          void stageRates.refetch();
        }}
      />
    );
  const { totals, stages, recentActivities } = dashboard.data;
  const rateOf = (stage: string) =>
    stageRates.data.find((item) => item.stage === stage)?.winProbability ?? 0;
  // Every ARR figure on the page follows the Weighted / Full toggle.
  const arrOf = (stage: string) =>
    stageArr(
      stages.find((item) => item.stage === stage)?.arrCents ?? 0,
      rateOf(stage),
      mode
    );
  const openArr = ['qualified', 'demo', 'trial'].reduce(
    (sum, stage) => sum + arrOf(stage),
    0
  );
  const cards = [
    {
      key: 'open_pipeline',
      value: money(openArr),
      sub: t('crm.workspace.open_count', { count: totals.openDeals }),
      Icon: TrendingUp,
      path: '/crm/deals',
    },
    {
      key: 'won_revenue',
      value: money(arrOf('won')),
      sub: t('crm.workspace.won_count', { count: totals.wonDeals }),
      Icon: Target,
      path: '/crm/deals?stage=won',
    },
    {
      key: 'companies',
      value: totals.companies.toLocaleString(),
      sub: t('crm.workspace.company_directory'),
      Icon: Building2,
      path: '/crm/companies',
    },
    {
      key: 'contacts',
      value: totals.contacts.toLocaleString(),
      sub: t('crm.workspace.contact_directory'),
      Icon: UsersRound,
      path: '/crm/contacts',
    },
  ];
  const maximum = Math.max(1, ...stages.map((stage) => stage.count));
  return (
    <div className={styles.dashboard}>
      <div className={styles.dashboardToolbar}>
        <div
          className={styles.valueSwitch}
          role="group"
          aria-label={t('crm.workspace.value_mode')}
        >
          {(['weighted', 'full'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => chooseMode(option)}
            >
              {t(`crm.workspace.value_${option}`)}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.metrics}>
        {cards.map(({ key, value, sub, Icon, path }) => (
          <button
            key={key}
            className={styles.metricCard}
            onClick={() => navigate(path)}
          >
            <div className={styles.metricLabel}>
              <span>{t(`crm.workspace.${key}`)}</span>
              <Icon />
            </div>
            <strong>{value}</strong>
            <span className={styles.metricSub}>
              {sub}
              <ArrowUpRight />
            </span>
          </button>
        ))}
      </div>
      <div className={styles.dashboardColumns}>
        <CrmAttention onDeal={onDeal} />
        <section className={styles.panel}>
          <CrmSectionTitle
            title={t('crm.workspace.pipeline_overview')}
            action={t('crm.workspace.view_pipeline')}
            onAction={() => navigate('/crm/deals')}
          />
          <p className={styles.panelCaption}>
            {t(
              mode === 'full'
                ? 'crm.workspace.pipeline_caption'
                : 'crm.workspace.pipeline_caption_weighted',
              { value: money(openArr) }
            )}
          </p>
          <div className={styles.pipelineBars}>
            {DEAL_STAGES.map((stage) => {
              const value = stages.find((item) => item.stage === stage);
              return (
                <button
                  key={stage}
                  className={styles.pipelineBarRow}
                  onClick={() => navigate(`/crm/deals?stage=${stage}`)}
                >
                  <span className={styles.pipelineStage}>
                    <i className={styles[`dot_${stage}`]} />
                    {t(`crm.workspace.stages.${stage}`)}
                  </span>
                  <span className={styles.barTrack}>
                    <span
                      className={`${styles.barFill} ${styles[`bar_${stage}`]}`}
                      style={{
                        width: `${((value?.count ?? 0) / maximum) * 100}%`,
                      }}
                    />
                  </span>
                  <strong>{value?.count ?? 0}</strong>
                  <span>{money(arrOf(stage))}</span>
                </button>
              );
            })}
          </div>
          {totals.openDeals === 0 && totals.wonDeals === 0 && (
            <div className={styles.panelFooter}>
              <span>{t('crm.workspace.pipeline_empty')}</span>
              <CrmButton
                variant="adminPrimary"
                IconPrefix={Plus}
                onClick={onCreate}
              >
                {t('crm.workspace.new_deal')}
              </CrmButton>
            </div>
          )}
        </section>
      </div>
      <section className={styles.panel}>
        <CrmSectionTitle
          title={t('crm.workspace.recent_activity')}
          action={t('crm.workspace.view_all')}
          onAction={() => navigate('/crm/activities')}
        />
        {recentActivities.length ? (
          <CrmActivityFeed
            activities={recentActivities}
            onDeal={onDeal}
            compact
          />
        ) : (
          <CrmEmpty
            title={t('crm.workspace.no_activities')}
            description={t('crm.workspace.activities_empty')}
          />
        )}
      </section>
    </div>
  );
};
